// App DOM instrumentation helper — the blueforge-org `data-appdom-*`
// convention (packages/dom/app-dom/README.md).
//
//   <header data-testid="topbar" {...appDom({ id: 'topbar', type: 'Header', scope: 'header' })}>
//
// The testid stays a literal JSX attribute because `testid-audit` finds
// testids by scanning source text; `appDomDrift.test.ts` fails when an
// appDom id has no literal `data-testid` of the same value next to it, so
// the two can't drift apart. Spread the result onto any element, including
// Radix `asChild` triggers.

export type AppDomType =
  | 'Screen'
  | 'Section'
  | 'Container'
  | 'Header'
  | 'Footer'
  | 'NavBar'
  | 'Sidebar'
  | 'Tabs'
  | 'Card'
  | 'Modal'
  | 'Drawer'
  | 'Tooltip'
  | 'Popover'
  | 'Dropdown'
  | 'Select'
  | 'Button'
  | 'IconButton'
  | 'Link'
  | 'Toggle'
  | 'Alert'
  | 'Toast'
  | 'Banner'
  | 'Badge'
  | 'List'
  | 'Chart'
  | 'KPI'
  | 'Avatar'
  | 'LogoMark'
  | 'Brand'
  | 'Text'
  | 'Heading'
  | 'Image'
  | 'Custom';

export type AppDomRole =
  | 'cta'
  | 'button'
  | 'heading'
  | 'input'
  | 'select'
  | 'image'
  | 'icon'
  | 'card'
  | 'list'
  | 'container'
  | 'text'
  | 'link'
  | 'other';

export type AppDomScope =
  | 'header'
  | 'hero'
  | 'nav'
  | 'sidebar'
  | 'main'
  | 'section'
  | 'aside'
  | 'footer'
  | 'dialog'
  | 'drawer'
  | 'toast'
  | 'banner'
  | 'menu';

export interface AppDomSpec {
  readonly id: string;
  readonly type?: AppDomType;
  readonly role?: AppDomRole;
  readonly scope?: AppDomScope;
  readonly label?: string;
}

export interface AppDomAttributes {
  readonly 'data-appdom-id': string;
  readonly 'data-appdom-type'?: AppDomType;
  readonly 'data-appdom-role'?: AppDomRole;
  readonly 'data-appdom-scope'?: AppDomScope;
  readonly 'data-appdom-label'?: string;
}

export function appDom({ id, type, role, scope, label }: AppDomSpec): AppDomAttributes {
  return {
    'data-appdom-id': id,
    ...(type ? { 'data-appdom-type': type } : {}),
    ...(role ? { 'data-appdom-role': role } : {}),
    ...(scope ? { 'data-appdom-scope': scope } : {}),
    ...(label ? { 'data-appdom-label': label } : {}),
  };
}
